import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  Paper,
  Button,
  Tooltip,
  Fab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import {
  ZoomIn,
  ZoomOut,
  Save,
  Add,
  Delete,
  Edit,
  DragIndicator,
  Block,
  TextFields,
  Undo,
} from '@mui/icons-material';
import { FieldMappingItem, ExcludeArea, FieldMapping } from '../../models';
import FieldEditDialog from './FieldEditDialog';

/**
 * bbox 좌표 변환 유틸리티 (OCS 호환)
 * bbox: [left, top, right, bottom] - 정규화 좌표 (0.0 ~ 1.0)
 * position: { left, top, width, height } - 퍼센트 기반 (0 ~ 100)
 */
const bboxToPosition = (bbox: [number, number, number, number]) => ({
  left: bbox[0] * 100,
  top: bbox[1] * 100,
  width: (bbox[2] - bbox[0]) * 100,
  height: (bbox[3] - bbox[1]) * 100,
});

const positionToBbox = (position: { left: number; top: number; width: number; height: number }): [number, number, number, number] => [
  position.left / 100,
  position.top / 100,
  (position.left + position.width) / 100,
  (position.top + position.height) / 100,
];

interface DraggableBoxProps {
  id: string;
  bbox: [number, number, number, number];
  label: string;
  color: string;
  containerWidth: number;
  containerHeight: number;
  isSelected: boolean;
  onSelect: () => void;
  onBboxChange: (bbox: [number, number, number, number]) => void;
  onDelete: () => void;
  onEdit?: () => void;
  showEditButton?: boolean;
}

const DraggableBox: React.FC<DraggableBoxProps> = ({
  id,
  bbox,
  label,
  color,
  containerWidth,
  containerHeight,
  isSelected,
  onSelect,
  onBboxChange,
  onDelete,
  onEdit,
  showEditButton = true,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeCorner, setResizeCorner] = useState<string>('');
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialBbox, setInitialBbox] = useState<[number, number, number, number]>([0, 0, 0, 0]);

  const position = bboxToPosition(bbox);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialBbox(bbox);
  };

  const handleResizeMouseDown = (e: React.MouseEvent, corner: string) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect();
    setIsResizing(true);
    setResizeCorner(corner);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialBbox(bbox);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging && !isResizing) return;

      const deltaX = (e.clientX - dragStart.x) / containerWidth;
      const deltaY = (e.clientY - dragStart.y) / containerHeight;

      if (isDragging) {
        const width = initialBbox[2] - initialBbox[0];
        const height = initialBbox[3] - initialBbox[1];
        const newLeft = Math.max(0, Math.min(1 - width, initialBbox[0] + deltaX));
        const newTop = Math.max(0, Math.min(1 - height, initialBbox[1] + deltaY));
        onBboxChange([newLeft, newTop, newLeft + width, newTop + height]);
      } else if (isResizing) {
        let [left, top, right, bottom] = initialBbox;

        if (resizeCorner.includes('e')) {
          right = Math.max(left + 0.02, Math.min(1, right + deltaX));
        }
        if (resizeCorner.includes('w')) {
          left = Math.max(0, Math.min(right - 0.02, left + deltaX));
        }
        if (resizeCorner.includes('s')) {
          bottom = Math.max(top + 0.02, Math.min(1, bottom + deltaY));
        }
        if (resizeCorner.includes('n')) {
          top = Math.max(0, Math.min(bottom - 0.02, top + deltaY));
        }

        onBboxChange([left, top, right, bottom]);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeCorner('');
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, resizeCorner, dragStart, initialBbox, containerWidth, containerHeight, onBboxChange]);

  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      sx={{
        position: 'absolute',
        top: `${position.top}%`,
        left: `${position.left}%`,
        width: `${position.width}%`,
        height: `${position.height}%`,
        border: isSelected ? `2px solid ${color}` : `1px solid ${color}`,
        borderRadius: '2px',
        backgroundColor: isSelected ? `${color}33` : `${color}1a`,
        cursor: isDragging ? 'grabbing' : 'grab',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        zIndex: isSelected ? 100 : 10,
        '&:hover': {
          backgroundColor: `${color}33`,
        },
      }}
      onMouseDown={handleMouseDown}
    >
      <Typography
        variant="caption"
        sx={{
          color: color,
          fontWeight: isSelected ? 600 : 400,
          fontSize: '10px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          px: 0.5,
          textShadow: '0 0 2px white',
        }}
      >
        {label}
      </Typography>

      {/* 리사이즈 핸들 (선택 시에만 표시) */}
      {isSelected && (
        <>
          {/* 모서리 핸들 */}
          {['nw', 'ne', 'sw', 'se'].map((corner) => (
            <Box
              key={corner}
              sx={{
                position: 'absolute',
                width: 8,
                height: 8,
                backgroundColor: color,
                cursor: `${corner}-resize`,
                borderRadius: '50%',
                ...(corner.includes('n') ? { top: -4 } : { bottom: -4 }),
                ...(corner.includes('w') ? { left: -4 } : { right: -4 }),
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, corner)}
            />
          ))}
          {/* 변 핸들 */}
          {['n', 'e', 's', 'w'].map((side) => (
            <Box
              key={side}
              sx={{
                position: 'absolute',
                backgroundColor: color,
                cursor: `${side === 'n' || side === 's' ? 'ns' : 'ew'}-resize`,
                ...(side === 'n' && { top: -3, left: '25%', width: '50%', height: 6 }),
                ...(side === 's' && { bottom: -3, left: '25%', width: '50%', height: 6 }),
                ...(side === 'e' && { right: -3, top: '25%', width: 6, height: '50%' }),
                ...(side === 'w' && { left: -3, top: '25%', width: 6, height: '50%' }),
                opacity: 0.6,
                '&:hover': { opacity: 1 },
              }}
              onMouseDown={(e) => handleResizeMouseDown(e, side)}
            />
          ))}
        </>
      )}

      {/* 선택 시 툴바 */}
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            top: -32,
            right: 0,
            display: 'flex',
            gap: 0.5,
            backgroundColor: 'white',
            borderRadius: 1,
            boxShadow: 1,
            p: 0.25,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {showEditButton && onEdit && (
            <IconButton size="small" onClick={onEdit} title="편집">
              <Edit fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" onClick={onDelete} color="error" title="삭제">
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

interface ImageTemplateEditorProps {
  imageUrl: string;
  initialFieldMapping: FieldMapping;
  onSave: (fieldMapping: FieldMapping) => void;
  onCancel?: () => void;
  width?: number;
}

export const ImageTemplateEditor: React.FC<ImageTemplateEditorProps> = ({
  imageUrl,
  initialFieldMapping,
  onSave,
  onCancel,
  width = 800,
}) => {
  const [scale, setScale] = useState<number>(1.0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const [fields, setFields] = useState<FieldMappingItem[]>(initialFieldMapping.fields || []);
  const [excludeAreas, setExcludeAreas] = useState<ExcludeArea[]>(initialFieldMapping.exclude_areas || []);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<'field' | 'exclude'>('field');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<FieldMappingItem | null>(null);
  const [history, setHistory] = useState<FieldMapping[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // 이미지 로드
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageSize({ width: img.width, height: img.height });
      setLoading(false);
      setError(null);
    };
    img.onerror = () => {
      setLoading(false);
      setError('이미지를 로드할 수 없습니다.');
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // 히스토리 저장
  const saveToHistory = useCallback(() => {
    setHistory((prev) => [...prev.slice(-20), { fields: [...fields], exclude_areas: [...excludeAreas] }]);
  }, [fields, excludeAreas]);

  // 실행 취소
  const handleUndo = useCallback(() => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setFields(prev.fields);
    setExcludeAreas(prev.exclude_areas || []);
    setHistory((h) => h.slice(0, -1));
  }, [history]);

  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.25, 3.0));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.25, 0.25));
  }, []);

  // 렌더링 크기 계산
  const aspectRatio = imageSize.height / imageSize.width || 1.414;
  const scaledWidth = width * scale;
  const scaledHeight = scaledWidth * aspectRatio;

  const handleAddField = () => {
    saveToHistory();
    const newField: FieldMappingItem = {
      field_id: `field_${Date.now()}`,
      hwpx_field: '',
      label: '새 필드',
      type: 'text',
      bbox: [0.1, 0.1, 0.3, 0.15],
      form_enabled: true,
    };
    setEditingField(newField);
    setEditDialogOpen(true);
  };

  const handleAddExcludeArea = () => {
    saveToHistory();
    const newArea: ExcludeArea = {
      bbox: [0.1, 0.1, 0.3, 0.2],
    };
    setExcludeAreas((prev) => [...prev, newArea]);
  };

  const handleFieldBboxChange = (fieldId: string, bbox: [number, number, number, number]) => {
    setFields((prev) =>
      prev.map((f) => (f.field_id === fieldId ? { ...f, bbox } : f))
    );
  };

  const handleExcludeAreaBboxChange = (index: number, bbox: [number, number, number, number]) => {
    setExcludeAreas((prev) => {
      const updated = [...prev];
      updated[index] = { bbox };
      return updated;
    });
  };

  const handleDeleteField = (fieldId: string) => {
    saveToHistory();
    setFields((prev) => prev.filter((f) => f.field_id !== fieldId));
    if (selectedId === fieldId) {
      setSelectedId(null);
    }
  };

  const handleDeleteExcludeArea = (index: number) => {
    saveToHistory();
    setExcludeAreas((prev) => prev.filter((_, i) => i !== index));
    if (selectedId === `exclude_${index}`) {
      setSelectedId(null);
    }
  };

  const handleEditField = (field: FieldMappingItem) => {
    setEditingField(field);
    setEditDialogOpen(true);
  };

  const handleSaveField = (field: FieldMappingItem) => {
    saveToHistory();
    setFields((prev) => {
      const existingIndex = prev.findIndex((f) => f.field_id === field.field_id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = field;
        return updated;
      }
      return [...prev, field];
    });
    setEditDialogOpen(false);
    setEditingField(null);
  };

  const handleSave = () => {
    onSave({
      fields,
      exclude_areas: excludeAreas.length > 0 ? excludeAreas : undefined,
    });
  };

  const handleContainerClick = () => {
    setSelectedId(null);
  };

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      {/* 이미지 영역 */}
      <Paper elevation={2} sx={{ flex: 1, p: 2 }}>
        {/* 컨트롤 바 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            p: 1,
            bgcolor: 'grey.100',
            borderRadius: 1,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={zoomOut} disabled={scale <= 0.25} size="small">
              <ZoomOut />
            </IconButton>
            <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
              {Math.round(scale * 100)}%
            </Typography>
            <IconButton onClick={zoomIn} disabled={scale >= 3.0} size="small">
              <ZoomIn />
            </IconButton>

            <Box sx={{ mx: 1, borderLeft: '1px solid', borderColor: 'grey.300', height: 24 }} />

            <ToggleButtonGroup
              value={editMode}
              exclusive
              onChange={(_, value) => value && setEditMode(value)}
              size="small"
            >
              <ToggleButton value="field">
                <TextFields sx={{ mr: 0.5 }} fontSize="small" />
                필드
              </ToggleButton>
              <ToggleButton value="exclude">
                <Block sx={{ mr: 0.5 }} fontSize="small" />
                제외영역
              </ToggleButton>
            </ToggleButtonGroup>

            <Tooltip title="실행 취소 (Ctrl+Z)">
              <span>
                <IconButton onClick={handleUndo} disabled={history.length === 0} size="small">
                  <Undo />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {onCancel && (
              <Button variant="outlined" size="small" onClick={onCancel}>
                취소
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={<Save />}
              onClick={handleSave}
            >
              저장
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 이미지 및 필드 오버레이 */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            overflow: 'auto',
            maxHeight: 'calc(100vh - 300px)',
          }}
        >
          <Box
            ref={containerRef}
            sx={{
              position: 'relative',
              border: '1px solid',
              borderColor: 'grey.300',
              bgcolor: 'white',
              boxShadow: 2,
              width: scaledWidth,
              height: scaledHeight,
            }}
            onClick={handleContainerClick}
          >
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: 'grey.50',
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {/* 템플릿 이미지 */}
            {!loading && (
              <img
                src={imageUrl}
                alt="Template"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  pointerEvents: 'none',
                }}
              />
            )}

            {/* 제외 영역 오버레이 */}
            {!loading &&
              excludeAreas.map((area, index) => (
                <DraggableBox
                  key={`exclude_${index}`}
                  id={`exclude_${index}`}
                  bbox={area.bbox}
                  label={`제외${index + 1}`}
                  color="#f44336"
                  containerWidth={scaledWidth}
                  containerHeight={scaledHeight}
                  isSelected={selectedId === `exclude_${index}`}
                  onSelect={() => setSelectedId(`exclude_${index}`)}
                  onBboxChange={(bbox) => handleExcludeAreaBboxChange(index, bbox)}
                  onDelete={() => handleDeleteExcludeArea(index)}
                  showEditButton={false}
                />
              ))}

            {/* 필드 오버레이 */}
            {!loading &&
              fields.map((field) => (
                <DraggableBox
                  key={field.field_id}
                  id={field.field_id}
                  bbox={field.bbox || [0.1, 0.1, 0.3, 0.15]}
                  label={field.label}
                  color="#1976d2"
                  containerWidth={scaledWidth}
                  containerHeight={scaledHeight}
                  isSelected={selectedId === field.field_id}
                  onSelect={() => setSelectedId(field.field_id)}
                  onBboxChange={(bbox) => handleFieldBboxChange(field.field_id, bbox)}
                  onDelete={() => handleDeleteField(field.field_id)}
                  onEdit={() => handleEditField(field)}
                />
              ))}
          </Box>
        </Box>
      </Paper>

      {/* 사이드 패널 */}
      <Paper elevation={2} sx={{ width: 300, p: 2 }}>
        {/* 필드 목록 */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            필드 ({fields.length})
          </Typography>
          <Tooltip title="필드 추가">
            <Fab color="primary" size="small" onClick={handleAddField}>
              <Add />
            </Fab>
          </Tooltip>
        </Box>

        <List dense sx={{ maxHeight: 250, overflow: 'auto', mb: 2 }}>
          {fields.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              필드가 없습니다.
            </Typography>
          ) : (
            fields.map((field) => (
              <ListItem
                key={field.field_id}
                button
                selected={selectedId === field.field_id}
                onClick={() => setSelectedId(field.field_id)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  border: '1px solid',
                  borderColor: selectedId === field.field_id ? 'primary.main' : 'transparent',
                }}
              >
                <DragIndicator sx={{ mr: 1, color: 'grey.400', fontSize: 18 }} />
                <ListItemText
                  primary={field.label}
                  secondary={
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                      <Chip label={field.type} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                      {field.ocr_enabled && <Chip label="OCR" size="small" color="success" sx={{ height: 18, fontSize: 10 }} />}
                      {field.form_enabled && <Chip label="Form" size="small" color="primary" sx={{ height: 18, fontSize: 10 }} />}
                    </Box>
                  }
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                />
                <ListItemSecondaryAction>
                  <IconButton size="small" onClick={() => handleEditField(field)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteField(field.field_id)} color="error">
                    <Delete fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))
          )}
        </List>

        <Divider sx={{ my: 2 }} />

        {/* 제외 영역 목록 */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            제외 영역 ({excludeAreas.length})
          </Typography>
          <Tooltip title="제외 영역 추가">
            <Fab color="error" size="small" onClick={handleAddExcludeArea}>
              <Add />
            </Fab>
          </Tooltip>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          OCR 처리 시 무시할 영역
        </Typography>

        <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
          {excludeAreas.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              제외 영역이 없습니다.
            </Typography>
          ) : (
            excludeAreas.map((area, index) => (
              <ListItem
                key={`exclude_${index}`}
                button
                selected={selectedId === `exclude_${index}`}
                onClick={() => setSelectedId(`exclude_${index}`)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  border: '1px solid',
                  borderColor: selectedId === `exclude_${index}` ? 'error.main' : 'transparent',
                }}
              >
                <Block sx={{ mr: 1, color: 'error.main', fontSize: 18 }} />
                <ListItemText
                  primary={`제외 영역 ${index + 1}`}
                  secondary={`[${area.bbox.map((v) => v.toFixed(2)).join(', ')}]`}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption', fontFamily: 'monospace' }}
                />
                <ListItemSecondaryAction>
                  <IconButton size="small" onClick={() => handleDeleteExcludeArea(index)} color="error">
                    <Delete fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))
          )}
        </List>
      </Paper>

      {/* 필드 편집 다이얼로그 */}
      <FieldEditDialog
        open={editDialogOpen}
        field={editingField}
        onSave={handleSaveField}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingField(null);
        }}
        showOcrOptions
      />
    </Box>
  );
};

export default ImageTemplateEditor;
